'use client';

import { setCookie, deleteCookie } from 'cookies-next';
import {
  signOut as _signOut,
  linkWithPopup as _linkWithPopup,
  updateProfile as _updateProfile,
  updatePassword as _updatePassword,
  signInWithPopup as _signInWithPopup,
  signInAnonymously as _signInAnonymously,
  EmailAuthProvider as _EmailAuthProvider,
  linkWithCredential as _linkWithCredential,
  GoogleAuthProvider as _GoogleAuthProvider,
  GithubAuthProvider as _GithubAuthProvider,
  TwitterAuthProvider as _TwitterAuthProvider,
  sendEmailVerification as _sendEmailVerification,
  sendPasswordResetEmail as _sendPasswordResetEmail,
  signInWithEmailAndPassword as _signInWithEmailAndPassword,
  createUserWithEmailAndPassword as _createUserWithEmailAndPassword,
} from 'firebase/auth';

import { AUTH, REFRESH_TOKEN_STORAGE_KEY } from 'src/lib/firebase';

// ----------------------------------------------------------------------

export const setSession = async (user) => {
  if (user) {
    await setCookie(REFRESH_TOKEN_STORAGE_KEY, user.refreshToken);
  } else {
    await deleteCookie(REFRESH_TOKEN_STORAGE_KEY);
  }
};

export const updateProfile = async (profile = { displayName: '', photoURL: '' }) => {
  try {
    const { currentUser } = AUTH;
    if (currentUser) {
      await _updateProfile(currentUser, profile);
      await setSession(currentUser);
    }
  } catch (error) {
    console.error('Error during update profile:', error);
    throw error;
  }
};

/** Sign in */
export const signInWithPassword = async ({ email, password }) => {
  try {
    const auth = await _signInWithEmailAndPassword(AUTH, email, password);
    await setSession(auth.user);
  } catch (error) {
    console.error('Error during sign in with password:', error);
    throw error;
  }
};

export const signInWithProvider = async (ProviderClass) => {
  const { currentUser } = AUTH;
  const provider = new ProviderClass();
  const auth = currentUser?.isAnonymous
    ? await _linkWithPopup(currentUser, provider)
    : await _signInWithPopup(AUTH, provider);
  await setSession(auth.user);
};

export const signInAnonymously = async () => await _signInAnonymously(AUTH);

export const signInWithX = () => signInWithProvider(_TwitterAuthProvider);
export const signInWithGithub = () => signInWithProvider(_GithubAuthProvider);
export const signInWithGoogle = () => signInWithProvider(_GoogleAuthProvider);

/** Sign up */
export const signUp = async ({ email, password, firstName, lastName }) => {
  try {
    const { currentUser } = AUTH;
    const auth = currentUser?.isAnonymous
      ? await _linkWithCredential(currentUser, _EmailAuthProvider.credential(email, password))
      : await _createUserWithEmailAndPassword(AUTH, email, password);
    await updateProfile({ displayName: `${firstName} ${lastName}` });
    await setSession(auth.user);
    await _sendEmailVerification(auth.user);
  } catch (error) {
    console.error('Error during sign up:', error);
    throw error;
  }
};

/** Sign out */
export const signOut = async () => {
  await _signOut(AUTH);
  await setSession(null);
};

/** Reset password */
export const sendPasswordResetEmail = async ({ email }) =>
  await _sendPasswordResetEmail(AUTH, email);

/** Change password */
export const chagePassword = async (oldPassword, newPassword) => {
  try {
    const { currentUser } = AUTH;
    if (currentUser?.email) {
      const auth = await _signInWithEmailAndPassword(AUTH, currentUser?.email, oldPassword);
      await _updatePassword(auth.user, newPassword);
    } else {
      throw new Error('Email required');
    }
  } catch (error) {
    console.error('Error during change password:', error);
    throw error;
  }
};
